import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { explainFunctionError, functionErrorResponse, useSupabase, useTenant } from "@how/app-kit";
import { semanticColor, type SemanticColor } from "@how/design-system";

/** The store (ADR 0047): products with variants, members' orders picked up at church or shipped, and its settings. */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

/** Cents as money for the screen. */
export function formatMoney(cents: number, currency = "usd"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// --------------------------------------------------------------------------------------------------- products

export interface VariantRow {
  readonly id: string;
  readonly name: string;
  readonly priceCents: number;
  /** Null when the church does not count this one. */
  readonly stock: number | null;
  readonly isActive: boolean;
}

/** How a member may receive a product: at church, by post, or whichever the church offers. */
export type ProductFulfilment = "pickup" | "ship" | "either";

export const FULFILMENT_LABEL: Record<ProductFulfilment, string> = {
  either: "Picked up or shipped",
  pickup: "Picked up only",
  ship: "Shipped only",
};

export interface ProductRow {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly imageAssetId: string | null;
  readonly imagePath: string | null;
  readonly imageAlt: string | null;
  readonly currency: string;
  readonly isActive: boolean;
  readonly fulfilment: ProductFulfilment;
  /** Sold in the Shop; false for something offered only as an event's add-on. */
  readonly inShop: boolean;
  readonly variants: readonly VariantRow[];
}

export function useProducts() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["products", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ProductRow[]> => {
      const { data, error } = await supabase
        .from("products")
        .select(
          "id, name, description, image_asset_id, currency, is_active, fulfilment, in_shop, content_assets(storage_path, alt_text), product_variants(id, name, price_cents, stock, is_active, sort_order, deleted_at)",
        )
        .eq("tenant_id", tenantId ?? "")
        .is("deleted_at", null)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        imageAssetId: p.image_asset_id,
        imagePath: p.content_assets?.storage_path ?? null,
        imageAlt: p.content_assets?.alt_text ?? null,
        currency: p.currency,
        isActive: p.is_active,
        fulfilment: p.fulfilment as ProductFulfilment,
        inShop: p.in_shop,
        variants: p.product_variants
          .filter((v) => v.deleted_at === null)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((v) => ({
            id: v.id,
            name: v.name,
            priceCents: v.price_cents,
            stock: v.stock,
            isActive: v.is_active,
          })),
      }));
    },
  });
}

function useInvalidateProducts() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["products", tenantId] });
  };
}

export function useSaveProduct() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: async (input: {
      readonly productId: string | null;
      readonly name: string;
      readonly description: string;
      readonly imageAssetId: string | null;
      readonly isActive: boolean;
      readonly fulfilment: ProductFulfilment;
      readonly inShop: boolean;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("set_product", {
        p_tenant_id: tenantId ?? "",
        p_name: input.name,
        p_description: input.description,
        ...(input.imageAssetId === null ? {} : { p_image_asset_id: input.imageAssetId }),
        p_is_active: input.isActive,
        p_fulfilment: input.fulfilment,
        p_in_shop: input.inShop,
        ...(input.productId === null ? {} : { p_product_id: input.productId }),
      });
      failed(error);
      return data as unknown as string;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteProduct() {
  const supabase = useSupabase();
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: async (productId: string) => {
      const { error } = await supabase.rpc("delete_product", { p_product_id: productId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useSaveVariant() {
  const supabase = useSupabase();
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: async (input: {
      readonly productId: string;
      readonly variantId: string | null;
      readonly name: string;
      readonly priceCents: number;
      readonly stock: number | null;
      readonly isActive: boolean;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("set_product_variant", {
        p_product_id: input.productId,
        p_name: input.name,
        p_price_cents: input.priceCents,
        ...(input.stock === null ? {} : { p_stock: input.stock }),
        p_is_active: input.isActive,
        ...(input.variantId === null ? {} : { p_variant_id: input.variantId }),
      });
      failed(error);
      return data as unknown as string;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteVariant() {
  const supabase = useSupabase();
  const invalidate = useInvalidateProducts();
  return useMutation({
    mutationFn: async (variantId: string) => {
      const { error } = await supabase.rpc("delete_product_variant", { p_variant_id: variantId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

// --------------------------------------------------------------------------------------------------- orders

export type OrderStatus = "pending_payment" | "paid" | "fulfilled" | "cancelled";

export type OrderFulfilment = "pickup" | "ship";

/** What an order's state means to the person at the pickup table or the post office. */
export function orderStatusLabel(status: OrderStatus, fulfilment: OrderFulfilment): string {
  switch (status) {
    case "pending_payment":
      return "Waiting for payment";
    case "paid":
      return fulfilment === "ship" ? "Paid, to ship" : "Paid, to pick up";
    case "fulfilled":
      return fulfilment === "ship" ? "Shipped" : "Picked up";
    case "cancelled":
      return "Cancelled";
  }
}

/**
 * Semantic roles, not raw colors: Mantine's stock blue is not a theme ramp, and its light badge label measured 3.64:1 on
 * its own fill in the light scheme (#253). Paid is work still to do, so it is `info`, like the portal's paid orders.
 */
export const ORDER_STATUS_COLOR: Record<OrderStatus, SemanticColor> = {
  pending_payment: semanticColor("warning"),
  paid: semanticColor("info"),
  fulfilled: semanticColor("success"),
  cancelled: semanticColor("neutral"),
};

export interface OrderItemRow {
  readonly product: string;
  readonly variant: string;
  readonly quantity: number;
  readonly unitPriceCents: number;
}

export interface ShipTo {
  readonly name?: string;
  readonly line1?: string;
  readonly line2?: string;
  readonly city?: string;
  readonly state?: string;
  readonly postalCode?: string;
  readonly country?: string;
}

/** An address as a label reads: name, street lines, "city, state postal code", country. */
export function addressLines(to: ShipTo): string[] {
  const place = [to.city, [to.state, to.postalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [to.name, to.line1, to.line2, place, to.country].filter(
    (line): line is string => line !== undefined && line !== "",
  );
}

export interface OrderRow {
  readonly id: string;
  readonly number: number;
  readonly status: OrderStatus;
  readonly personName: string;
  /**
   * A guest's order from the church website (ADR 0076): the email the buyer typed, for a receipt or a question; null
   * for a member's order. Their name is in `personName`, with "(guest)" until the order is matched to a person.
   */
  readonly guestEmail: string | null;
  readonly totalCents: number;
  readonly refundedCents: number;
  readonly currency: string;
  readonly createdAt: string;
  readonly paidAt: string | null;
  readonly fulfilledAt: string | null;
  readonly cancelReason: string | null;
  readonly fulfilment: OrderFulfilment;
  readonly shippingCents: number;
  readonly taxCents: number;
  readonly shipTo: ShipTo | null;
  readonly carrier: string | null;
  readonly trackingNumber: string | null;
  /** An event's add-ons: the event and date they are for (ADR 0047 step 3); null for a Shop order. */
  readonly forEvent: {
    readonly title: string;
    readonly startsAt: string;
    /** The campus's zone: an event's time is wall-clock time where it happens (ADR 0036). */
    readonly timeZone: string | null;
  } | null;
  readonly items: readonly OrderItemRow[];
}

/** What the member was charged: the order's total and the tax Stripe added. */
export const chargedCents = (o: OrderRow): number => o.totalCents + o.taxCents;

/** Who bought: the person, or a guest from the website by the name they typed (ADR 0076). */
export function orderBuyer(
  person: { first_name: string; last_name: string; preferred_name: string | null } | null,
  guestName: string | null,
): string {
  if (person !== null) return `${person.preferred_name ?? person.first_name} ${person.last_name}`;
  if (guestName !== null) return `${guestName} (guest)`;
  return "Someone no longer in the directory";
}

export function useOrders(status: OrderStatus | "open" | "all") {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["orders", tenantId, status],
    enabled: tenantId !== null,
    queryFn: async (): Promise<OrderRow[]> => {
      let query = supabase
        .from("orders")
        .select(
          "id, number, status, total_cents, refunded_cents, currency, created_at, paid_at, fulfilled_at, cancel_reason, fulfilment, shipping_cents, tax_cents, ship_to, carrier, tracking_number, guest_name, guest_email, people(first_name, last_name, preferred_name), order_items(product_name, variant_name, quantity, unit_price_cents, created_at), event_registrations(event_occurrences(starts_at, events(title, campuses(time_zone))))",
        )
        .eq("tenant_id", tenantId ?? "")
        .order("created_at", { ascending: false })
        .limit(300);
      if (status === "open") query = query.in("status", ["pending_payment", "paid"]);
      else if (status !== "all") query = query.eq("status", status);
      const { data, error } = await query;
      if (error) throw error;
      return data.map((o) => ({
        id: o.id,
        number: o.number,
        status: o.status,
        personName: orderBuyer(o.people, o.guest_name),
        guestEmail: o.guest_email,
        totalCents: o.total_cents,
        refundedCents: o.refunded_cents,
        currency: o.currency,
        createdAt: o.created_at,
        paidAt: o.paid_at,
        fulfilledAt: o.fulfilled_at,
        cancelReason: o.cancel_reason,
        fulfilment: o.fulfilment as OrderFulfilment,
        shippingCents: o.shipping_cents,
        taxCents: o.tax_cents,
        shipTo: (o.ship_to as ShipTo | null) ?? null,
        carrier: o.carrier,
        trackingNumber: o.tracking_number,
        forEvent:
          o.event_registrations === null
            ? null
            : {
                title: o.event_registrations.event_occurrences.events.title,
                startsAt: o.event_registrations.event_occurrences.starts_at,
                timeZone:
                  o.event_registrations.event_occurrences.events.campuses?.time_zone ?? null,
              },
        items: [...o.order_items]
          .sort((a, b) => a.created_at.localeCompare(b.created_at))
          .map((i) => ({
            product: i.product_name,
            variant: i.variant_name,
            quantity: i.quantity,
            unitPriceCents: i.unit_price_cents,
          })),
      }));
    },
  });
}

function useInvalidateOrders() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["orders", tenantId] });
    await client.invalidateQueries({ queryKey: ["products", tenantId] });
  };
}

/**
 * Hands over a paid pickup order, from Store → Orders or, for an event's add-ons, the event roster (#264): the same
 * `fulfil_order`, which checks commerce.fulfill_orders itself and refuses an unpaid or already handed-over order.
 * Orders, the roster and the person's record re-read, so each says "handed over", and after a refusal too, so none
 * keeps a stale Hand over button (#277).
 */
export function useFulfilOrder() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase.rpc("fulfil_order", { p_order_id: orderId });
      failed(error);
    },
    onSettled: async () => {
      await invalidate();
      await client.invalidateQueries({ queryKey: ["event-registrations", tenantId] });
      await client.invalidateQueries({ queryKey: ["person", tenantId] });
    },
  });
}

/**
 * Undoes a hand-over (#280): a picked-up order goes back to paid, from Store → Orders or the event roster. Neither
 * refunds nor restocks; `unfulfil_order` checks commerce.fulfill_orders and refuses a shipped order. The roster and the
 * person's record re-read, so each stops saying "handed over", and after a refusal too (#277).
 */
export function useUnfulfilOrder() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: async (input: { readonly orderId: string }) => {
      const { error } = await supabase.rpc("unfulfil_order", { p_order_id: input.orderId });
      failed(error);
    },
    onSettled: async () => {
      await invalidate();
      await client.invalidateQueries({ queryKey: ["event-registrations", tenantId] });
      await client.invalidateQueries({ queryKey: ["person", tenantId] });
    },
  });
}

/** Marks a shipped order shipped, or corrects its carrier and tracking number afterwards. */
export function useShipOrder() {
  const supabase = useSupabase();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: async (input: {
      readonly orderId: string;
      readonly carrier: string;
      readonly trackingNumber: string;
    }) => {
      const { error } = await supabase.rpc("ship_order", {
        p_order_id: input.orderId,
        p_carrier: input.carrier.trim(),
        p_tracking_number: input.trackingNumber.trim(),
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useCancelOrder() {
  const supabase = useSupabase();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase.rpc("cancel_order", { p_order_id: orderId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/** A refund on the church's own Stripe account, through the stripe-refund function (ADR 0009). */
export function useRefundOrder() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: async (input: {
      readonly orderId: string;
      readonly amountCents: number;
      readonly reason: string;
    }) => {
      const result = await supabase.functions.invoke("stripe-refund", {
        body: {
          tenant_id: tenantId ?? "",
          order_id: input.orderId,
          amount_cents: input.amountCents,
          ...(input.reason.trim() === "" ? {} : { reason: input.reason.trim() }),
        },
      });
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
    },
    onSuccess: invalidate,
  });
}

// --------------------------------------------------------------------------------------------------- settings

/** Where the church's members shop (ADR 0047 step 4). */
export type StoreMode = "how" | "shopify" | "none";

export interface StoreSettings {
  readonly mode: StoreMode;
  /** The church's own Shopify store, when it sends members there. */
  readonly shopifyUrl: string;
  readonly pickupEnabled: boolean;
  readonly pickupNote: string;
  readonly shippingEnabled: boolean;
  readonly shippingRateCents: number;
  /** Null when shipping is never free. */
  readonly freeShippingOverCents: number | null;
  readonly shipCountries: readonly string[];
  /** Stripe Tax at checkout (owner decision 2026-09-26: off by default). */
  readonly taxEnabled: boolean;
}

/** What a church that never saved its settings has: pickup only, no tax. */
export const DEFAULT_STORE_SETTINGS: StoreSettings = {
  mode: "how",
  shopifyUrl: "",
  pickupEnabled: true,
  pickupNote: "",
  shippingEnabled: false,
  shippingRateCents: 0,
  freeShippingOverCents: null,
  shipCountries: ["US"],
  taxEnabled: false,
};

export function useStoreSettings() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["store-settings", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<StoreSettings> => {
      const { data, error } = await supabase
        .from("store_settings")
        .select(
          "store_mode, shopify_url, pickup_enabled, pickup_note, shipping_enabled, shipping_rate_cents, free_shipping_over_cents, ship_countries, tax_enabled",
        )
        .eq("tenant_id", tenantId ?? "")
        .maybeSingle();
      if (error) throw error;
      if (data === null) return DEFAULT_STORE_SETTINGS;
      return {
        mode: data.store_mode as StoreMode,
        shopifyUrl: data.shopify_url ?? "",
        pickupEnabled: data.pickup_enabled,
        pickupNote: data.pickup_note ?? "",
        shippingEnabled: data.shipping_enabled,
        shippingRateCents: data.shipping_rate_cents,
        freeShippingOverCents: data.free_shipping_over_cents,
        shipCountries: data.ship_countries,
        taxEnabled: data.tax_enabled,
      };
    },
  });
}

export function useSaveStoreSettings() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: StoreSettings) => {
      const mode = await supabase.rpc("set_store_mode", {
        p_tenant_id: tenantId ?? "",
        p_mode: input.mode,
        ...(input.mode === "shopify" ? { p_shopify_url: input.shopifyUrl.trim() } : {}),
      });
      failed(mode.error);
      const { error } = await supabase.rpc("set_store_settings", {
        p_tenant_id: tenantId ?? "",
        p_pickup_enabled: input.pickupEnabled,
        p_shipping_enabled: input.shippingEnabled,
        p_shipping_rate_cents: input.shippingRateCents,
        ...(input.freeShippingOverCents === null
          ? {}
          : { p_free_shipping_over_cents: input.freeShippingOverCents }),
        p_ship_countries: [...input.shipCountries],
        p_pickup_note: input.pickupNote,
        p_tax_enabled: input.taxEnabled,
      });
      failed(error);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["store-settings", tenantId] });
    },
  });
}
